// src/pages/FaxContacts.jsx
// Contacts (3.25.0; "Fax Contacts" until 3.26.0): the client's book of fax numbers. Every number it
// sent a fax to or received one from is here automatically, with counts;
// people add numbers by hand, name them, add a note, star them (the same ★
// priority as Outbox and Failed Sent) or remove them. Each number also has
// its phone listing (the caller name from the phone network, the same for
// every client); it's shown until the client gives the number its own name,
// and under it afterwards. Only Super Admins can correct a listing. Each
// client sees only its own book; Super Admins see every client's.
import { fax as faxOk } from '../utils/phone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import EmptyState from '../components/EmptyState';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import MultiSearchSelect from '../components/MultiSearchSelect';
import PriorityStar from '../components/PriorityStar';
import { API_BASE } from '../firebase/config';
import { downloadSheet } from '../utils/api';
import { formatFax, formatDate, formatCardDate } from '../utils/format';
import { PAGE_TITLE, PILL, PILL_ON, PILL_ROW, TH_SELECT, TH_OPTION } from '../theme/tabs';
import Loader from '../components/Loader';
import { usePhone } from '../hooks/useWindowWidth';
import Emo from '../components/Emo';
import { useProblemNumbers } from '../hooks/useProblemNumbers';
import { ClassBadge } from './ProblemNumbers';
import { useClientWord } from '../hooks/useClientWord';
import ContactFaxes from '../components/ContactFaxes';
import MenuDialog, { MD } from '../components/MenuDialog';

const last10 = n => String(n || '').replace(/\D/g, '').slice(-10);
// Delivered % across both ways (3.25.2): faxes that went through (sent and
// delivered, plus received) out of all finished ones (those plus failed sends
// and failed receives). Faxes still going out don't count yet.
const doneCount = r => r.delivered + r.failed + r.received + r.receiveFailed;
// How a contact is listed in the search box: "+1 (617) 246-7168 · Blue Cross MA appeals"
const optionOf = r => (r.name ? `${formatFax(r.number)} · ${r.name}` : formatFax(r.number));
// Does a contact match a search term (a picked contact, or typed text)?
function matchesTerm(r, t) {
  if (t === optionOf(r)) return true;
  const q = String(t).trim().toLowerCase(), qd = q.replace(/\D/g, '');
  return r.name.toLowerCase().includes(q) || r.note.toLowerCase().includes(q) || (r.listingName || '').toLowerCase().includes(q)
    || (qd.length >= 3 && /^[\d\s()+.-]+$/.test(q) && last10(r.number).includes(qd.slice(-10)));
}
// Who added (by hand) or named the contact, and when, for hovering (3.29.1)
const whoName = (name, email) => (name ? `${name} (${email})` : email);
function whoText(r) {
  const when = iso => formatDate(iso);   // the person's time zone, like every date in the portal
  const lines = [];
  if (r.manual && r.createdBy) lines.push(`Added by ${whoName(r.createdByName, r.createdBy)}${r.createdAt ? ` on ${when(r.createdAt)}` : ''}`);
  else if (r.ownName && r.createdBy) lines.push(`Named by ${whoName(r.createdByName, r.createdBy)}${r.createdAt ? ` on ${when(r.createdAt)}` : ''}`);
  if (r.updatedBy && r.updatedAt && r.updatedAt !== r.createdAt) lines.push(`Last changed by ${whoName(r.updatedByName, r.updatedBy)} on ${when(r.updatedAt)}`);
  return lines.join('\n');
}
const pct = r => (doneCount(r) ? Math.round(((r.delivered + r.received) / doneCount(r)) * 100) : null);
const pctTitle = r => (doneCount(r) ? `${r.delivered + r.received} of ${doneCount(r)} went through: ${r.delivered} of ${r.delivered + r.failed} sent, ${r.received} of ${r.received + r.receiveFailed} received` : 'No finished faxes yet');

export default function FaxContacts() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const problemOf = useProblemNumbers();   // 3.60.0
  const phone = usePhone();   // 3.51.0: one card per contact on phones
  const { getToken, can, shows, accountVersion } = useAuth();
  const { notify, confirm } = useNotify();
  const [rows, setRows]       = useState([]);
  const [stars, setStars]     = useState({ global: new Set(), clients: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [view, setView]       = useState('all');      // all | priority | unnamed | suggested | removed
  const [terms, setTerms]     = useState([]);           // the search box: picked contacts or typed words (3.25.2)
  const [client, setClient]   = useState('');
  const [show, setShow]       = useState('50');
  const [editing, setEditing] = useState(null);       // row id
  const [draft, setDraft]     = useState({ name: '', note: '', listing: '' });
  const [canFixListing, setCanFixListing] = useState(false);
  const [adding, setAdding]   = useState(null);       // { number, name, note, accountId }
  const [busy, setBusy]       = useState('');
  // 3.71.0 (owner): a count clicked → the list of those faxes
  const [faxesOf, setFaxesOf] = useState(null);   // { contact, kind }
  const countBtn = (r, kind, n, extra = {}) => (n
    ? <button type="button" onClick={() => setFaxesOf({ contact: r, kind })} style={{ ...S.countBtn, ...extra }} aria-label={`See the ${n} ${kind} fax${n === 1 ? '' : 'es'} with ${formatFax(r.number)}`}>{n}</button>
    : '—');
  const canEdit = shows('contacts.edit');   // 3.62.16: not in read-only View as
  const canRemove = shows('contacts.remove');   // 3.26.0: Admins and Super Admins

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE}/portal/contacts`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't load the contacts.");
      setRows(j.data || []);
      setCanFixListing(!!j.canFixListing);
      setStars({ global: new Set((j.global || []).map(last10)), clients: Object.fromEntries(Object.entries(j.stars || {}).map(([id, l]) => [id, new Set(l.map(last10))])) });
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [getToken]);
  useEffect(() => { load(); }, [load, accountVersion]);

  const clients = useMemo(() => {
    const m = new Map();
    rows.forEach(r => m.set(r.accountId, r.accountName));
    return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [rows]);
  const multi = clients.length > 1;
  const starred = r => stars.global.has(last10(r.number)) || !!stars.clients[r.accountId]?.has(last10(r.number));

  const counts = useMemo(() => {
    const live = rows.filter(r => !r.removed && (!client || r.accountId === client));
    return {
      all: live.length, priority: live.filter(starred).length, unnamed: live.filter(r => !r.name).length, added: live.filter(r => r.manual).length,
      suggested: live.filter(r => r.suggested).length, removed: rows.filter(r => r.removed && (!client || r.accountId === client)).length,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, client, stars]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (client && r.accountId !== client) return false;
      if (view === 'removed' ? !r.removed : r.removed) return false;
      if (view === 'priority' && !starred(r)) return false;
      if (view === 'unnamed' && r.name) return false;
      if (view === 'suggested' && !r.suggested) return false;
      if (view === 'added' && !r.manual) return false;   // added by hand by the client's people (3.29.1)
      return !terms.length || terms.some(t => matchesTerm(r, t));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, client, view, terms, stars]);

  const searchOptions = useMemo(() => [...new Set(rows.filter(r => (!client || r.accountId === client) && (view === 'removed' ? r.removed : !r.removed)).map(optionOf))], [rows, client, view]);
  const { sorted, sort, onSort } = useSort(filtered, {
    star: r => (starred(r) ? 0 : 1), client: r => r.accountName, name: r => r.name, number: r => last10(r.number),
    sent: r => r.sent, received: r => r.received + r.receiveFailed, failed: r => r.failed + r.receiveFailed, success: r => pct(r) ?? '', last: r => r.last || '',
  });
  const { pageRows: shown, pager } = usePager(sorted, show);   // page by page (3.26.0)

  // A contact's name (or the boxes to change it) and its buttons: the same in
  // the table and, on phones (3.51.0), in the cards
  const nameBlock = (r, isEditing) => (
    isEditing ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder={r.listingName ? `Your name (empty: ${r.listingName})` : 'Name'} aria-label="Your name for this number" maxLength={60} style={S.input} autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') saveEdit(r); if (e.key === 'Escape') setEditing(null); }} />
                          <input value={draft.note} onChange={e => setDraft({ ...draft, note: e.target.value })} placeholder="Note (optional)" aria-label="Note" maxLength={200} style={S.input}
                            onKeyDown={e => { if (e.key === 'Enter') saveEdit(r); if (e.key === 'Escape') setEditing(null); }} />
                          {canFixListing && (
                            <label style={S.listingEdit}><Emo e="📞" /> Phone listing, for every client (Super Admins only)
                              <input value={draft.listing} onChange={e => setDraft({ ...draft, listing: e.target.value })} placeholder={r.listingChecked ? 'No listing found' : 'Not looked up yet'} aria-label="Phone listing for every client" maxLength={60} style={{ ...S.input, marginTop: 3 }}
                                onKeyDown={e => { if (e.key === 'Enter') saveEdit(r); if (e.key === 'Escape') setEditing(null); }} />
                            </label>
                          )}
                        </div>
                      ) : (
                        <>
                          {r.name ? <span style={{ fontWeight: 600 }} title={whoText(r) || undefined}>{r.name}</span>
                            : <span style={{ color: 'var(--t-90a4ae)' }} title={r.listingChecked ? 'The phone network has no name for this number. Click Edit to add one.' : 'Its phone listing is looked up within a few minutes'}>{r.listingChecked ? 'Not listed' : 'Looking up…'}</span>}
                          {r.suggested && (r.listingFixed
                            ? <span style={S.suggested} title="A name saved for this number. Click Edit to give it your own name.">saved name</span>
                            : <span style={S.suggested} title="The name this number is listed under in the phone network. Click Edit to give it your own name.">phone listing</span>)}
                          {r.ownName && r.listingName && r.listingName.toLowerCase() !== r.ownName.toLowerCase() && (
                            <div style={S.meta} title="The name this number is listed under in the phone network"><Emo e="📞" /> {r.listingName}</div>
                          )}
                          {r.manual && <span style={S.manual} title={whoText(r) || 'Added by hand'}>added</span>}
                          {r.note && <div style={S.meta}>{r.note}</div>}
                        </>
                      )
  );
  const actions = (r, isEditing) => (
    canEdit && (isEditing ? (
                        <>
                          <button onClick={() => saveEdit(r)} disabled={busy === r.id} style={S.primarySmall}>{busy === r.id ? 'Saving…' : 'Save'}</button>
                          <button onClick={() => setEditing(null)} style={{ ...S.actionBtn, marginLeft: 6 }}>Cancel</button>
                        </>
                      ) : r.removed ? (
                        canRemove ? <button onClick={() => setRemoved(r, false)} disabled={busy === r.id} style={S.actionBtn}>Restore</button> : null
                      ) : (
                        <>
                          <button onClick={() => { setEditing(r.id); setDraft({ name: r.ownName, note: r.note, listing: r.listingName }); }} style={S.actionBtn}>✎ Edit</button>
                          {canRemove && <button onClick={() => setRemoved(r, true)} disabled={busy === r.id} style={{ ...S.actionBtn, marginLeft: 6 }} title="Remove from Contacts" aria-label={`Remove ${r.name || formatFax(r.number)}`}>✕</button>}
                        </>
                      ))
  );

  async function save(body, okTitle) {
    const r = await fetch(`${API_BASE}/portal/contacts`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || "Couldn't save that.");
    if (okTitle) notify({ tone: 'success', title: okTitle });
    await load();
  }
  async function saveEdit(r) {
    setBusy(r.id);
    try {
      // Super Admins: the phone listing, for every client
      if (canFixListing && draft.listing.trim() !== r.listingName) {
        const res = await fetch(`${API_BASE}/portal/contacts/listing`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ number: r.number, name: draft.listing }) });
        const j = await res.json();
        if (!res.ok) throw new Error(j.error || "Couldn't save the phone listing.");
      }
      await save({ accountId: r.accountId, number: r.number, name: draft.name, note: draft.note }, 'Contact saved');
      setEditing(null);
    } catch (e) { notify({ tone: 'error', title: "Couldn't save the contact", detail: e.message }); }
    setBusy('');
  }
  async function setRemoved(r, removed) {
    if (removed) {
      const ok = await confirm({ title: 'Remove this contact?', message: `${r.name || formatFax(r.number)} is hidden from Contacts${multi ? ` for ${r.accountName}` : ''}, even when faxes to or from it come in. Its faxes aren't affected, and you can bring it back from Removed.`, confirmLabel: 'Remove' });
      if (!ok) return;
    }
    setBusy(r.id);
    try { await save({ accountId: r.accountId, number: r.number, removed }, removed ? 'Contact removed' : 'Contact restored'); }
    catch (e) { notify({ tone: 'error', title: "Couldn't change it", detail: e.message }); }
    setBusy('');
  }
  // 3.77.5 (owner): Add contact is a pop-up (AddContactDialog). → throws a
  // plain message for the dialog to show; no notice (the dialog says it)
  async function saveNew(c) {
    const d = last10(c.number);
    if (d.length !== 10 || !faxOk(c.number).ok) throw new Error('Enter a valid US or Canada fax number.');   // 3.49.17: real area codes too
    if (!c.name.trim()) throw new Error('Enter a name for this contact.');
    if (multi && !c.accountId) throw new Error(`Pick the ${clientWord.toLowerCase()} this contact is for.`);
    const existing = rows.find(r => last10(r.number) === d && (!multi || r.accountId === c.accountId));
    if (existing && !existing.removed) throw new Error(`${formatFax(existing.number)} is already in Contacts${existing.name ? ` as ${existing.name}` : ''}. Use Edit on it to change it.`);
    await save({ add: true, accountId: c.accountId || undefined, number: d, name: c.name, note: c.note }, null);
    return { number: d, name: c.name.trim() };
  }
  async function downloadXlsx() {
    try {
      await downloadSheet('Contacts', [
        ...(multi ? [{ header: clientWord, get: r => r.accountName, width: 16 }] : []),
        { header: 'Name', get: r => r.ownName, width: 30 },
        { header: 'Phone listing', get: r => r.listingName, width: 24 },
        { header: 'Fax number', get: r => formatFax(r.number), width: 18 },
        { header: 'Priority', get: r => (starred(r) ? '★' : ''), width: 8 },
        { header: 'Sent', get: r => r.sent, width: 7 },
        { header: 'Sent delivered', get: r => r.delivered, width: 9 },
        { header: 'Sent failed', get: r => r.failed, width: 7 },
        { header: 'Received', get: r => r.received, width: 9 },
        { header: 'Received failed', get: r => r.receiveFailed, width: 9 },
        { header: 'Delivered %', get: r => (pct(r) == null ? '' : pct(r)), width: 9 },
        { header: 'Last fax', get: r => (r.last ? formatDate(r.last) : ''), width: 24 },
        { header: 'Note', get: r => r.note, width: 36 },
      ], sorted, getToken);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  const cols = 9 + (multi ? 1 : 0);
  return (
    <div>
      <div style={S.top}>
        <div>
          <h2 style={S.title} className="cp-title"><Emo e="📇" /> Contacts</h2>
          <p style={S.sub}>Every fax number {multi ? 'each company' : 'your company'} sent a fax to or received one from is added here by itself. Numbers show their phone listing until you give them your own name; your names and notes are only for your team. ★ marks a priority number: failed faxes to it get 2 retries instead of 1.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canEdit && <button onClick={() => setAdding({ number: '', name: '', note: '', accountId: client || '' })} style={S.primaryBtn}>＋ Add contact</button>}
          {can('fax.reports') && <button onClick={downloadXlsx} disabled={!sorted.length} style={{ ...S.darkBtn, opacity: sorted.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Download (.xlsx)</button>}
          <button onClick={load} style={S.darkBtn}><Emo e="↻" /> Refresh</button>
        </div>
      </div>

      <div style={S.filters}>
        <div style={{ minWidth: 300 }}>
          <MultiSearchSelect values={terms} onChange={setTerms} options={searchOptions} placeholder="Search a name, note or number" />
        </div>
        <div style={PILL_ROW} role="tablist" aria-label="Show">
          {[['all', 'All'], ['priority', '★ Priority'], ['unnamed', 'Not listed'], ['suggested', 'Phone listing only'], ['added', 'Added by people'], ...(counts.removed ? [['removed', 'Removed']] : [])].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)} style={{ ...PILL, ...(view === id ? PILL_ON : {}) }}>
              {label} <span style={{ opacity: 0.85 }}>{counts[id]}</span>
            </button>
          ))}
        </div>
        <ShowSelect value={show} onChange={setShow} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
          labelStyle={{ fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 }} style={S.select} />
      </div>

      {adding && <AddContactDialog start={adding} multi={multi} clients={clients} clientWord={clientWord} rows={rows} onSave={saveNew} onClose={() => setAdding(null)} />}

      {error && <div role="alert" style={S.error}>{error}</div>}
      {phone && multi && (
        <select value={client} onChange={e => setClient(e.target.value)} style={{ ...S.input, width: '100%', marginBottom: 10 }} aria-label={clientWord}>
          <option value="">All clients</option>
          {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      )}
      {phone ? (
        <div style={S.cards}>
          {loading ? <Loader label="Loading contacts…" />
            : !shown.length ? <EmptyState filtered={view !== 'all' || terms.length > 0 || !!client} onClear={() => { setView('all'); setTerms([]); setClient(''); }} clearLabel="Show all"
              filteredText="No contacts match." emptyText="No fax numbers yet. They're added here when faxes are sent or received, or add one with Add contact." />
            : shown.map(r => {
              const isEditing = editing === r.id;
              const p = pct(r);
              // 3.71.0: each count opens its faxes
              const counts = [r.sent > 0 && ['sent', 'Sent', r.sent], (r.received + r.receiveFailed) > 0 && ['received', 'Received', r.received + r.receiveFailed],
                (r.failed + r.receiveFailed) > 0 && ['failed', 'Failed', r.failed + r.receiveFailed]].filter(Boolean);
              return (
                <div key={r.id} style={{ ...S.mCard, opacity: r.removed ? 0.7 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <PriorityStar fax={{ toNumber: r.number, accountId: r.accountId, accountName: r.accountName }} map={stars} onChanged={load} />
                    <div style={{ flex: 1, minWidth: 0 }}>{nameBlock(r, isEditing)}</div>
                    {r.last && !isEditing && <span style={S.mWhen}>{formatCardDate(r.last)}</span>}
                  </div>
                  <div style={S.mMeta}><span style={{ fontFamily: 'monospace' }}>{formatFax(r.number)}</span>{multi && ` · ${r.accountName}`}</div>
                  {problemOf(r.number) && <div style={{ marginTop: 4 }}><ClassBadge {...problemOf(r.number)} /></div>}
                  {(counts.length > 0 || p != null) && <div style={{ ...S.mMeta, display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center' }}>
                    {counts.map(([k, label, n]) => <button key={k} type="button" onClick={() => setFaxesOf({ contact: r, kind: k })} style={{ ...S.countBtn, fontSize: 12.5 }}>{label} {n}</button>)}
                    {p != null && <span>{p}% delivered</span>}
                  </div>}
                  {canEdit && <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>{actions(r, isEditing)}</div>}
                </div>
              );
            })}
        </div>
      ) : (
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <SortTh k="star" label="★" sort={sort} onSort={onSort} style={{ ...S.th, width: 36 }} />
              {multi && (
                <th style={S.th}>
                  <select value={client} onChange={e => setClient(e.target.value)} style={TH_SELECT} aria-label={clientWord}>
                    <option value="" style={TH_OPTION}>All clients</option>
                    {clients.map(([id, name]) => <option key={id} value={id} style={TH_OPTION}>{name}</option>)}
                  </select>
                </th>
              )}
              <SortTh k="name" label="Name" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="number" label="Fax number" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="sent" label="Sent" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="received" label="Received" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="failed" label="Failed" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="success" label="Delivered" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="last" label="Last fax" sort={sort} onSort={onSort} style={S.th} />
              <th style={S.th}><span style={S.srOnly}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={cols} style={S.empty}><Loader label="Loading contacts…" /></td></tr>
              : !shown.length ? <tr><td colSpan={cols} style={S.empty}>
                <EmptyState filtered={view !== 'all' || terms.length > 0 || !!client} onClear={() => { setView('all'); setTerms([]); setClient(''); }} clearLabel="Show all"
                  filteredText="No contacts match." emptyText="No fax numbers yet. They're added here when faxes are sent or received, or add one with Add contact." />
              </td></tr>
              : shown.map((r, i) => {
                const isEditing = editing === r.id;
                const p = pct(r);
                return (
                  <tr key={r.id} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)', opacity: r.removed ? 0.7 : 1 }}>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <PriorityStar fax={{ toNumber: r.number, accountId: r.accountId, accountName: r.accountName }} map={stars} onChanged={load} />
                    </td>
                    {multi && <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)' }}>{r.accountName}</td>}
                    <td style={{ ...S.td, minWidth: 200 }}>
                      {nameBlock(r, isEditing)}
                    </td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 13 }}>{formatFax(r.number)}
                      {/* 3.60.0: on the Problem numbers list (Logs) */}
                      {problemOf(r.number) && <div style={{ fontFamily: 'inherit', marginTop: 4 }}><ClassBadge {...problemOf(r.number)} /></div>}</td>
                    <td style={S.td} title={r.sent ? `${r.sent} sent: ${r.delivered} delivered, ${r.failed} failed. Click to see them.` : undefined}>{countBtn(r, 'sent', r.sent)}</td>
                    <td style={S.td} title={r.received + r.receiveFailed ? `${r.received + r.receiveFailed} received: ${r.received} came through, ${r.receiveFailed} failed. Click to see them.` : undefined}>{countBtn(r, 'received', r.received + r.receiveFailed)}</td>
                    <td style={{ ...S.td, color: r.failed + r.receiveFailed ? 'var(--t-c62828)' : 'var(--t-90a4ae)', fontWeight: r.failed + r.receiveFailed ? 600 : 400 }}
                      title={r.failed + r.receiveFailed ? `${r.failed} sent, ${r.receiveFailed} received. Click to see them.` : undefined}>{countBtn(r, 'failed', r.failed + r.receiveFailed, { color: 'var(--t-c62828)' })}</td>
                    <td title={pctTitle(r)} style={{ ...S.td, fontWeight: 600, color: p == null ? 'var(--t-90a4ae)' : p >= 90 ? 'var(--t-2e7d32)' : p >= 60 ? 'var(--t-e65100)' : 'var(--t-c62828)' }}>{p == null ? '—' : `${p}%`}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{r.last ? formatDate(r.last) : '—'}</td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {actions(r, isEditing)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      )}
      {!loading && <Pager {...pager} label="contacts" />}
      {faxesOf && <ContactFaxes contact={faxesOf.contact} kind={faxesOf.kind} multi={multi} onClose={() => setFaxesOf(null)} />}
    </div>
  );
}

// 3.77.5 (owner, Nov 2): ＋ Add contact opens this pop-up instead of a box
// under the buttons. Fax number, name and note (and the company for
// Super Admins); the number is checked as you type (a real US / Canada
// number, not already in Contacts). After Save it says the contact was
// added and offers ＋ Add another (same company, empty boxes) or Done.
function AddContactDialog({ start, multi, clients, clientWord, rows, onSave, onClose }) {
  const [c, setC] = useState(start);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [added, setAdded] = useState(null);   // the contact just saved: the "added" screen
  const [count, setCount] = useState(0);      // how many added in this pop-up
  const set = patch => { setC(x => ({ ...x, ...patch })); setErr(''); };
  const d = last10(c.number);
  const valid = d.length === 10 && faxOk(c.number).ok;
  const existing = valid && rows.find(r => last10(r.number) === d && !r.removed && (!multi || r.accountId === c.accountId));
  async function submit(e) {
    e?.preventDefault();
    setBusy(true); setErr('');
    try { setAdded(await onSave(c)); setCount(n => n + 1); }
    catch (x) { setErr(x.message); }
    setBusy(false);
  }
  const another = () => { setC({ number: '', name: '', note: '', accountId: c.accountId }); setAdded(null); setErr(''); };
  const hint = { fontSize: 12, marginTop: 5 };
  return (
    <MenuDialog id="add-contact" icon="📇" title={added ? 'Contact added' : 'New contact'} onClose={onClose} width={520}
      sub={added ? null : 'Save a fax number with a name your team will recognise. It shows under the number in Outbox, Inbox and Compose.'}>
      {added ? (
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <div aria-hidden="true" style={{ width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%', background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)', display: 'grid', placeItems: 'center', fontSize: 28, fontWeight: 800 }}>✓</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--t-263238)' }}>{added.name}</div>
          <div style={{ fontSize: 14, color: 'var(--t-546e7a)', marginTop: 2, fontFamily: 'ui-monospace, Menlo, monospace' }}>{formatFax(added.number)}</div>
          <p style={{ ...MD.muted, margin: '12px 0 18px' }}>is in Contacts{count > 1 ? ` (${count} added so far)` : ''}. Would you like to add another one?</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={another} style={MD.primary} autoFocus>＋ Add another contact</button>
            <button onClick={onClose} style={MD.secondary}>Done</button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          {multi && <>
            <label htmlFor="ac-client" style={{ ...MD.label, marginTop: 4 }}>{clientWord}</label>
            <select id="ac-client" value={c.accountId} onChange={e => set({ accountId: e.target.value })} style={MD.input}>
              <option value="">Pick one…</option>
              {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </>}
          <label htmlFor="ac-number" style={{ ...MD.label, ...(multi ? {} : { marginTop: 4 }) }}>Fax number</label>
          <input id="ac-number" value={c.number} onChange={e => set({ number: e.target.value })} placeholder="e.g. (555) 010-0100" inputMode="tel" autoFocus style={MD.input} />
          {c.number.trim() && (existing
            ? <div style={{ ...hint, color: 'var(--t-e65100)' }}>Already in Contacts{existing.name ? ` as ${existing.name}` : ''}.</div>
            : valid ? <div style={{ ...hint, color: 'var(--t-2e7d32)' }}>✓ {formatFax(d)}</div>
            : d.length >= 10 ? <div style={{ ...hint, color: 'var(--t-c62828)' }}>That isn’t a US or Canada fax number.</div> : null)}
          <label htmlFor="ac-name" style={MD.label}>Name</label>
          <input id="ac-name" value={c.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. Riverside Family Practice" maxLength={60} style={MD.input} />
          <label htmlFor="ac-note" style={MD.label}>Note <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
          <input id="ac-note" value={c.note} onChange={e => set({ note: e.target.value })} placeholder="e.g. Appeals only, ask for Dana" maxLength={200} style={MD.input} />
          {err && <div role="alert" style={MD.error}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
            <button type="button" onClick={onClose} style={MD.secondary}>Cancel</button>
            <button type="submit" disabled={busy || !!existing} style={{ ...MD.primary, opacity: busy || existing ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save contact'}</button>
          </div>
        </form>
      )}
    </MenuDialog>
  );
}

const S = {
  countBtn: { background: 'none', border: 'none', padding: '2px 4px', margin: '-2px -4px', borderRadius: 6, cursor: 'pointer', font: 'inherit', color: 'var(--t-00796b)', fontWeight: 700, textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 },
  // 3.51.0: phones
  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  mCard: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.05)' },
  mWhen: { fontSize: 11.5, color: 'var(--t-78909c)', flexShrink: 0, marginTop: 2 },
  mMeta: { fontSize: 12.5, color: 'var(--t-607d8b)', marginTop: 4, paddingLeft: 30 },
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' },
  title: { ...PAGE_TITLE, margin: '0 0 4px' },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5, maxWidth: 720 },
  darkBtn: { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  primaryBtn: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  primarySmall: { padding: '6px 12px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  filters: { display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' },
  search: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', minWidth: 240 },
  select: { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, marginBottom: 10, fontSize: 13 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-546e7a)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  meta: { fontSize: 11, color: 'var(--t-78909c)', marginTop: 2 },
  suggested: { marginLeft: 8, padding: '1px 8px', borderRadius: 10, background: 'var(--c-fff8e1)', color: 'var(--t-e65100)', fontSize: 11, fontWeight: 700 },
  listingEdit: { display: 'grid', fontSize: 11, color: 'var(--t-78909c)', fontWeight: 600 },
  manual: { marginLeft: 8, padding: '1px 8px', borderRadius: 10, background: 'var(--c-e3f2fd)', color: 'var(--t-0d47a1)', fontSize: 11, fontWeight: 700 },
  actionBtn: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  empty: { textAlign: 'center', padding: '36px 0', color: 'var(--t-90a4ae)', fontSize: 14 },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
};
